import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../utils/api';
import { useAuth } from './AuthContext';
import config from '../config';
import { requestFCMToken, onForegroundMessage } from '../firebase';

// 通知コンテキストを作成
const NotificationContext = createContext();

// 通知コンテキストを使用するためのカスタムフック
export function useNotification() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  // 状態定義
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [pollingActive, setPollingActive] = useState(false);
  const [sseConnected, setSSEConnected] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  // PWA インストール用（beforeinstallprompt を捕捉）
  const [isInstallable, setIsInstallable] = useState(false);
  const deferredPromptRef = useRef(null);

  // 認証情報の取得
  const { isLoggedIn: isAuthenticated, token } = useAuth();
  const lastFetchRef = useRef(0);
  const isPollingActiveRef = useRef(false);

  // 未読カウントを取得する関数（軽量: unread-count のみ）
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const now = Date.now();
      // 最低2秒間隔を空ける（連続呼び出し防止）
      if (now - lastFetchRef.current < 2000) return true;
      lastFetchRef.current = now;

      // apiService.get はレスポンスボディを直接返す（response.data ではない）
      const response = await apiService.get('/api/notifications/unread-count');
      const data = response?.data || response || {};
      if (data.count !== undefined) {
        setUnreadCount(data.count);
      }
      if (data.message_count !== undefined) {
        setUnreadMessageCount(data.message_count);
      }
      setLastUpdated(now);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // 通知ページ用: 最新通知を取得して recentNotifications を更新
  const fetchRecentNotifications = useCallback(async () => {
    try {
      // apiService.get はレスポンスボディを直接返す
      const response = await apiService.get('/api/notifications/combined-poll');
      const data = response?.data || response || {};
      if (data.unread_count !== undefined) {
        setUnreadCount(data.unread_count);
      }
      // combined-poll は unread_messages を常に 0 で返すため、
      // メッセージ未読数はここで上書きしない（unread-count 側で正しく取得する）
      if (data.notifications && data.notifications.length > 0) {
        setRecentNotifications(data.notifications);
      }
      setLastUpdated(Date.now());
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // 未読通知カウントを更新する関数（外部から直接設定用）
  const updateNotificationCount = useCallback((count) => {
    setUnreadCount(count);
    setLastUpdated(Date.now());
  }, []);

  // 未読メッセージカウントを更新する関数
  const updateMessageCount = useCallback((count) => {
    setUnreadMessageCount(count);
    setLastUpdated(Date.now());
  }, []);

  // すべてのカウントを更新する関数
  const updateAllCounts = useCallback((notificationCount, messageCount) => {
    setUnreadCount(notificationCount);
    setUnreadMessageCount(messageCount);
    setLastUpdated(Date.now());
  }, []);

  // 通知を追加する関数（ローカル即時反映）
  const addNotification = useCallback((type, data) => {
    setRecentNotifications(prev => {
      if (data.id && prev.some(n => n.id === data.id)) return prev;
      const notificationData = {
        ...data,
        created_at: data.created_at || new Date().toISOString(),
        sender: data.sender || { id: 0, username: 'system', display_name: 'システム', avatar: '', verified: true }
      };
      return [{ type, data: notificationData, timestamp: new Date().toISOString() }, ...prev].slice(0, 10);
    });
    // ローカルカウントをインクリメント
    setUnreadCount(prev => prev + 1);
  }, []);

  // プッシュ通知権限をリクエストする関数
  const requestPushPermission = useCallback(async () => {
    try {
      if (!('Notification' in window)) return 'unsupported';
      if (Notification.permission === 'granted') { setPushPermission('granted'); return 'granted'; }
      if (Notification.permission === 'denied') { setPushPermission('denied'); return 'denied'; }
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      return permission;
    } catch (error) {
      return 'default';
    }
  }, []);

  // プッシュ通知購読: FCM トークンを取得・登録する
  const subscribeToPush = useCallback(async () => {
    try {
      if (!('Notification' in window)) return false;
      // 許可をリクエスト（未許可の場合）
      if (Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        setPushPermission(perm);
        if (perm !== 'granted') return false;
      }
      // FCM トークンを取得してバックエンドに登録
      const t = await requestFCMToken();
      return !!t;
    } catch (e) {
      console.warn('subscribeToPush error:', e);
      return false;
    }
  }, []);
  // 後方互換のエイリアス
  const subscribeToNotifications = subscribeToPush;
  const unsubscribeFromPush = useCallback(async () => {
    try {
      // ローカルの FCM トークンを破棄（以降のポーリング判定にも影響）
      localStorage.removeItem('fcmToken');
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // ─── PWA インストール ───
  const installPWA = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return false;
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      deferredPromptRef.current = null;
      setIsInstallable(false);
      return choice && choice.outcome === 'accepted';
    } catch (e) {
      console.warn('installPWA error:', e);
      return false;
    }
  }, []);

  // PWA の状態を返す（Settings 画面用）。ネットワーク不要のローカル判定。
  const getPWAStatus = useCallback(async () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    return {
      status: 'success',
      pwaStatus: {
        installable: !!deferredPromptRef.current,
        installed: isStandalone,
        display_mode: isStandalone ? 'standalone' : 'browser',
      },
    };
  }, []);

  // ブラウザ通知を表示する関数
  const showBrowserNotification = useCallback((title, message) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const notification = new Notification(title, { body: message, icon: '/logo192.png' });
      notification.onclick = () => { window.focus(); notification.close(); };
    } catch (error) {}
  }, []);

  const startPolling = useCallback(() => setPollingActive(true), []);
  const stopPolling = useCallback(() => setPollingActive(false), []);

  // PWA インストールプロンプトを捕捉（ブラウザ表示時に「アプリを入れる」導線を出すため）
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setIsInstallable(true);
    };
    const onInstalled = () => {
      deferredPromptRef.current = null;
      setIsInstallable(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // SSE接続（プレースホルダー: 現在は機能しない）
  const connectSSE = useCallback(async () => false, []);

  // ─── ライフサイクル ───

  // ログイン時に初回カウント取得 + FCMトークン登録
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCounts();
      // FCMトークンを取得してバックエンドに登録
      requestFCMToken().catch(() => {});
    } else {
      setUnreadCount(0);
      setUnreadMessageCount(0);
      setRecentNotifications([]);
    }
  }, [isAuthenticated, fetchUnreadCounts]);

  // FCMフォアグラウンドメッセージ受信 → バッジ即時更新
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = onForegroundMessage((payload) => {
      console.log('🔔 FCM foreground:', payload);
      const data = payload.data || {};
      const type = data.type;

      if (type === 'message') {
        setUnreadMessageCount(prev => prev + 1);
      } else {
        setUnreadCount(prev => prev + 1);
      }

      // フォアグラウンド受信時も通知を表示する。
      // 注意: Android Chrome では new Notification() が使えず TypeError になるため、
      // Service Worker の registration.showNotification() を使う（PC/Android 両対応）。
      if (Notification.permission === 'granted') {
        // 送信側は data のみで送るため data から title/body を取り出す
        const title = data.title || payload.notification?.title || 'DNS SNS';
        const body = data.body || payload.notification?.body || '新しい通知があります';
        const options = { body, icon: '/logo192.png', badge: '/logo192.png', data, tag: type || 'default' };
        (async () => {
          try {
            if ('serviceWorker' in navigator) {
              // FCM 専用 SW（別スコープ）を優先、無ければ既定の登録を使う
              const reg = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope')
                || await navigator.serviceWorker.ready;
              if (reg) {
                await reg.showNotification(title, options);
                return;
              }
            }
            // フォールバック（PC ブラウザ等）
            new Notification(title, options);
          } catch (e) {
            console.warn('フォアグラウンド通知の表示に失敗:', e);
          }
        })();
      }
    });

    return unsubscribe;
  }, [isAuthenticated, fetchUnreadCounts]);

  // タブがアクティブに戻った時にカウントを更新（FCM の有無に関わらず即時同期）
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCounts();
      }
    };

    const handleFocus = () => {
      fetchUnreadCounts();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, fetchUnreadCounts]);

  // グローバル未読カウントポーリング（全ページで動作）
  // FCM は取りこぼし（非フォアグラウンド・SW未受信・配信失敗）があるため、
  // FCM 有効時も軽量な unread-count ポーリングを保険として併用する。
  // FCM 有効時は間隔を長め(30秒)、非対応時は短め(15秒)。
  useEffect(() => {
    if (!isAuthenticated) return;
    const fcmEnabled = !!localStorage.getItem('fcmToken');
    const intervalMs = fcmEnabled ? 30000 : 15000;

    // 初回即時取得
    fetchUnreadCounts();

    const interval = setInterval(() => {
      // タブが非表示のときはスキップ（無駄な通信を避ける）
      if (document.visibilityState === 'visible') {
        fetchUnreadCounts();
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCounts]);

  // pollingActive が true の時のみ 60秒間隔でポーリング
  // （FCM未対応かつ通知ページが開かれている間だけ有効）
  useEffect(() => {
    isPollingActiveRef.current = pollingActive;
    if (!isAuthenticated || !pollingActive) return;
    const fcmEnabled = !!localStorage.getItem('fcmToken');
    if (fcmEnabled) return; // FCM有効ならポーリング不要

    const interval = setInterval(() => {
      if (isPollingActiveRef.current) {
        fetchRecentNotifications();
      }
    }, 60000); // 60秒間隔（通知ページ表示中のみ）

    return () => clearInterval(interval);
  }, [isAuthenticated, pollingActive, fetchRecentNotifications]);

  // コンテキスト値
  const value = {
    unreadCount,
    unreadMessageCount,
    lastUpdated,
    recentNotifications,
    pushPermission,
    updateNotificationCount,
    updateMessageCount,
    updateAllCounts,
    showBrowserNotification,
    fetchUnreadCounts,
    fetchRecentNotifications,
    pollingActive,
    isPollingActive: pollingActive,
    sseConnected,
    setPollingActive,
    startPolling,
    stopPolling,
    requestPushPermission,
    subscribeToNotifications,
    subscribeToPush,
    unsubscribeFromPush,
    addNotification,
    connectSSE,
    // PWA インストール
    isInstallable,
    installPWA,
    getPWAStatus,
    // リアルタイム接続状態（SSE は無効化済みのため false 相当。Messages 等の後方互換用）
    realtimeConnected: sseConnected,
    eventSource: null,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}